import { useMemo, useRef } from 'react';
import { gradeReach, readingsAt, threatsTo, worstOf } from '../../engine/cover/coverCue';
import { standingAt } from '../../engine/battlefield/tokens';
import { indexReach, tileAt, tileKey } from '../../engine/battlefield/movement';
import { bandFor } from '../../engine/battlefield/bands';
import { hasCondition } from '../../engine/conditions';
import { useDebouncedResult } from '../../hooks/useDebouncedResult';

/** How long the ghost must hold one tile before its screens rise (decision 8): a drag in passing shows the feet. */
const SETTLE_MS = 120;

const PACE_WORDS = { half: 'Half move', full: 'Full move' };

const same = (key) => key;

/** The far edge of the Full band, where the grading stops (the user: Half and Full only). */
export function fullBandLimit(bands, movement) {
  return bands?.find((b) => b.key === 'full')?.limit ?? movement?.full ?? 0;
}

/** "Half move, 3.4 m", or null past the Full band. */
export function paceOf(cost, bands) {
  const band = bands?.length ? bandFor(cost, bands) : null;
  return PACE_WORDS[band] ? `${PACE_WORDS[band]}, ${cost.toFixed(1)} m` : null;
}

/** What a gun's reading depends on of where it stands: a token write elsewhere (a heading drag) changes none. */
const threatKey = (threats) =>
  threats
    .map(({ id, token: t }) => `${id}@${t.x},${t.z},${t.elevation},${t.height},${t.footprint},${!!t.ignoresTerrain}`)
    .join('|');

/**
 * The actor's guns - the SAME array while none of them moved, so a write to any other token (or a turn of his own
 * heading) does not re-grade the whole reach (the review).
 */
function useActorThreats(on, panel) {
  const { roster, tokens, actorId } = panel;
  const fresh = useMemo(() => {
    const actor = on && actorId ? roster?.find((c) => c.id === actorId) : null;
    return actor && tokens ? threatsTo(actor, roster, tokens) : [];
  }, [on, roster, tokens, actorId]);
  const kept = useRef({ key: '', threats: fresh });
  const key = threatKey(fresh);
  if (kept.current.key !== key) kept.current = { key, threats: fresh };
  return kept.current.threats;
}

/** The reach graded tile by tile while it is the actor's, and a counter that changes with each new grading. */
function useGraded(panel, geo, movement, threats) {
  const { tokens, actorId, relationRules, isGm } = panel;
  const { reach, movementBands, movingMovement } = movement;
  const actorToken = tokens?.[actorId];
  const theirs = movement.movingId === actorId && threats.length > 0 && !!actorToken;
  const tileSize = geo.boundary.tileSize;
  const graded = useMemo(() => {
    if (!theirs || !reach?.length) return null;
    const options = {
      maxCost: fullBandLimit(movementBands, movingMovement),
      tileSize,
      rules: relationRules,
      includeHidden: isGm,
    };
    return gradeReach(reach, actorToken, threats, geo.solids, options);
  }, [theirs, reach, movementBands, movingMovement, actorToken, threats, geo.solids, relationRules, isGm, tileSize]);
  const run = useRef({ graded: null, n: 0 });
  if (run.current.graded !== graded) run.current = { graded, n: run.current.n + 1 };
  return { graded, ripple: run.current.n % 2 ? 'a' : 'b', gradeMax: fullBandLimit(movementBands, movingMovement) };
}

/**
 * The readings at the ghost's settled tile, or at the reach tile under the pointer. They do not move with the
 * camera: the scene projects the ghost's screens for its own camera (useCoverCue `coverOnScreen`).
 */
function useSpotReading(panel, geo, movement, threats, spot) {
  const { tokens, actorId, relationRules, isGm } = panel;
  const actorToken = tokens?.[actorId];
  return useMemo(() => {
    if (!spot || !actorToken || !threats.length) return null;
    const readings = readingsAt(spot, actorToken, threats, geo.solids, { rules: relationRules, includeHidden: isGm });
    const pace = paceOf(spot.cost ?? 0, movement.movementBands);
    return { token: standingAt(actorToken, spot), readings, worst: worstOf(readings), pace };
  }, [spot, actorToken, threats, geo.solids, relationRules, isGm, movement.movementBands]);
}

/** The actor made a Tactical Advance: he counts as in cover for this move (2026-10-26), and the readout says so. */
function actorAdvancing({ roster, actorId }) {
  const actor = actorId ? roster?.find((c) => c.id === actorId) : null;
  return hasCondition(actor?.state?.conditions, 'Ta');
}

/**
 * useCoverReach - the take-cover cue while moving (2026-10-25): the actor's reach graded by the worst cover each
 * spot gives (the Half and Full bands, open unshaded), the ghost's readings once it settles (the scene projects its
 * screens), and the readout for the ghost or for the reach tile under the pointer. Off the map's own data; nothing
 * is stored.
 */
export default function useCoverReach({ on, panel, geo, movement, drag, ground }) {
  const threats = useActorThreats(on, panel);
  const { graded, ripple, gradeMax } = useGraded(panel, geo, movement, threats);
  const tileSize = geo.boundary.tileSize;
  // One index per reach: a pointer's tile is then one lookup, not a scan of every tile per move (the review).
  const index = useMemo(() => indexReach(movement.reach ?? [], tileSize), [movement.reach, tileSize]);
  const carried = on && drag.carried?.id === panel.actorId ? drag.carried : null;
  const carriedTile = carried ? tileAt(index, carried, tileSize) : null;
  const carriedKey = carriedTile ? tileKey(carriedTile.x, carriedTile.z, tileSize) : null;
  const settledKey = useDebouncedResult(carriedKey, same, SETTLE_MS).result;
  const ghostSpot = carriedKey && settledKey === carriedKey ? carriedTile : null;
  const hovered = !carried && graded ? tileAt(index, ground, tileSize) : null;
  const hoveredSpot = hovered && graded.has(tileKey(hovered.x, hovered.z, tileSize)) ? hovered : null;
  const ghost = useSpotReading(panel, geo, movement, threats, ghostSpot);
  const pointed = useSpotReading(panel, geo, movement, threats, hoveredSpot);
  const advancing = actorAdvancing(panel);
  return { graded, ripple, gradeMax, ghost, readout: ghost ?? pointed, carriedId: carried?.id ?? null, advancing };
}
